/**
 * useKitchenChime
 * -----------------
 * Plays a repeating chime whenever a new order lands in the kitchen queue
 * (status = "sent_to_production"). The chime CONTINUES ringing every N seconds
 * until either:
 *   (a) the employee clicks "Snooze" (silences until a new order arrives), or
 *   (b) the employee marks the order as "Pulled" (moves it out of the
 *       sent_to_production status → queue is empty → chime stops).
 *
 * Uses the Web Audio API directly so no static audio asset is required.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";

const CHIME_INTERVAL_MS = 6000;
const POLL_INTERVAL_MS = 10000;

function playTone() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    [
      { freq: 880,  start: now,        dur: 0.18 },
      { freq: 1320, start: now + 0.14, dur: 0.22 },
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = "sine";
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.start(start);
      osc.stop(start + dur + 0.02);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) { console.warn("[useKitchenChime] audio unavailable", e); }
}

export default function useKitchenChime(enabled = true) {
  const [snoozed, setSnoozed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const seenIds = useRef(new Set());
  const primed = useRef(false);
  const intervalRef = useRef(null);

  // Poll the sent_to_production queue on an interval. Two things happen:
  //   1. If new orders appear that we haven't seen before → chime + toast.
  //   2. `pendingCount` always reflects the CURRENT queue size, so the
  //      repeating chime interval below will stop automatically when the
  //      kitchen employee marks everything as Pulled.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/orders?status=sent_to_production");
        if (cancelled) return;
        const newOrders = data.filter((o) => !seenIds.current.has(o.id));
        for (const o of data) seenIds.current.add(o.id);
        setPendingCount(data.length);

        // Prime once so we don't ring on initial mount for existing work
        if (!primed.current) { primed.current = true; return; }

        // Auto-reset snooze when queue clears so the NEXT order rings again
        if (data.length === 0 && snoozed) setSnoozed(false);

        // Announce brand-new work (not while already snoozed)
        if (newOrders.length > 0 && !snoozed) {
          playTone();
          toast(`${newOrders.length} new order${newOrders.length > 1 ? "s" : ""} in kitchen`, {
            description: newOrders.map((o) => o.order_no).join(", "),
            duration: 8000,
            action: { label: "Snooze", onClick: () => setSnoozed(true) },
          });
        }
      } catch (e) { console.warn("[useKitchenChime] poll failed", e?.message || e); }
    };
    tick();
    const t = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [enabled, snoozed]);

  // Repeating chime — keeps ringing every N seconds while there's pending
  // production work and the user hasn't snoozed. Stops the instant the queue
  // is empty (i.e. the employee has moved everything to Pulled).
  useEffect(() => {
    if (!enabled || snoozed || pendingCount === 0) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    intervalRef.current = setInterval(() => playTone(), CHIME_INTERVAL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [enabled, snoozed, pendingCount]);

  return {
    snoozed,
    hasPending: pendingCount > 0,
    pendingCount,
    snooze: () => setSnoozed(true),
    resume: () => setSnoozed(false),
  };
}
